'use client';

import type { DefaultValues } from 'react-hook-form';
import type { ZodSchema } from 'zod';

import {
	useRegistrationActions, useRegistrationStore, useUpdateProfile,
} from '@/modules/authentication/application';
import { RegistrationSteps } from '@/modules/authentication/presentation/components';

import * as RegistrationStepSchema from '../models/registration-step.form-model';

type RegistrationStep<T> = {
	component:      React.ComponentType
	defaultValues?: DefaultValues<T>
	key:            string
	schema:         ZodSchema<T>
	title:          string

	formSubmit: (data: T) => Promise<string | void> | string | void
};

const createRegistrationStep = <T>(step: RegistrationStep<T>) => step;

export const useRegistrationSteps = () => {
	const actions = useRegistrationActions();
	const registrationStore = useRegistrationStore();
	const { execute } = useUpdateProfile();
	const employmentStatus = registrationStore.registration?.workHistory?.employmentStatus ?? 'NO_EXPERIENCE';
	const {
		getEducation,
		getJobPreference,
		getPersonalInfo,
		getWorkHistory,
	} = registrationStore;

	const workExperienceStep = [
		createRegistrationStep({
			component:     RegistrationSteps.JobChangedCount,
			defaultValues: (() => ({ jobChangeCount: getWorkHistory()?.jobChangeCount }))(),
			formSubmit:    actions.submitJobChangedCount,
			key:           'job-changed-count',
			schema:        RegistrationStepSchema.JobChangedCountStepSchema,
			title:         'Please select the number of job changes',
		}),
		createRegistrationStep({
			component:     RegistrationSteps.CurrentCompany,
			defaultValues: (() => ({ companyName: '', hideCompanyName: getWorkHistory()?.hideCompanyName ?? true }))(),
			formSubmit:    actions.submitCurrentCompany,
			key:           'current-company',
			schema:        RegistrationStepSchema.CurrentCompanyStepSchema as never,
			title:         'Please enter current company name that you work or worked',
		}),
		createRegistrationStep({
			component:  RegistrationSteps.JobIndustry,
			formSubmit: actions.submitJobIndustry,
			key:        'job-industry',
			schema:     RegistrationStepSchema.JobIndustryStepSchema,
			title:      'Please select job industry',
		}),
		createRegistrationStep({
			component:  RegistrationSteps.JobOccupation,
			formSubmit: actions.submitJobOccupation,
			key:        'job-occupation',
			schema:     RegistrationStepSchema.JobOccupationStepSchema as never,
			title:      'Please select your job occupation',
		}),
	];

	const registrationSteps = [
		createRegistrationStep({
			component:     RegistrationSteps.DateOfBirth,
			defaultValues: (() => ({ dateOfBirth: getPersonalInfo()?.dateOfBirth ?? '' }))(),
			formSubmit:    actions.submitDateOfBirth,
			key:           'date-of-birth',
			schema:        RegistrationStepSchema.DateOfBirthStepSchema,
			title:         'Please enter your date of birth',
		}),
		createRegistrationStep({
			component:     RegistrationSteps.Gender,
			defaultValues: (() => ({ gender: getPersonalInfo()?.gender ?? '' }))(),
			formSubmit:    actions.submitGender,
			key:           'gender',
			schema:        RegistrationStepSchema.GenderStepSchema,
			title:         'Please select gender',
		}),
		createRegistrationStep({
			component:     RegistrationSteps.PhoneNumber,
			defaultValues: (() => ({ phoneNumber: getPersonalInfo()?.phoneNumber ?? '' }))(),
			formSubmit:    actions.submitPhoneNumber,
			key:           'phone-number',
			schema:        RegistrationStepSchema.PhoneNumberStepSchema,
			title:         'Please enter your phone number',
		}),
		createRegistrationStep({
			component:     RegistrationSteps.Residence,
			defaultValues: (() => ({ provinceId: getPersonalInfo()?.provinceId ?? '' }))(),
			formSubmit:    actions.submitResidence,
			key:           'residence',
			schema:        RegistrationStepSchema.ResidenceStepSchema,
			title:         'Please select your residence area',
		}),
		createRegistrationStep({
			component:     RegistrationSteps.Education,
			defaultValues: (() => ({ educationLevel: getEducation()?.educationLevel }))(),
			formSubmit:    actions.submitEducation,
			key:           'education',
			schema:        RegistrationStepSchema.EducationStepSchema,
			title:         'Please select your highest education level',
		}),
		createRegistrationStep({
			component:     RegistrationSteps.GraduationStatus,
			defaultValues: (() => ({ graduationStatus: getEducation()?.graduationStatus }))(),
			formSubmit:    actions.submitGraduationStatus,
			key:           'graduation-status',
			schema:        RegistrationStepSchema.GraduationStatusStepSchema,
			title:         'Please select graduation status',
		}),
		createRegistrationStep({
			component:     RegistrationSteps.EmploymentStatus,
			defaultValues: (() => ({ employmentStatus: getWorkHistory()?.employmentStatus }))(),
			formSubmit:    actions.submitEmploymentStatus,
			key:           'employment-status',
			schema:        RegistrationStepSchema.EmploymentStatusStepSchema,
			title:         'Please select your current employed status',
		}),
		...(employmentStatus === 'NO_EXPERIENCE' ? [] : workExperienceStep),
		createRegistrationStep({
			component:     RegistrationSteps.AnnualSalary,
			formSubmit:    actions.submitAnnualSalary,
			key:           'annual-salary',
			schema:        RegistrationStepSchema.AnnualSalaryStepSchema,
			title:         'Please enter your annual salary',
			defaultValues: (() => ({
				currentAnnualSalary:  getJobPreference()?.currentAnnualSalary,
				expectedAnnualSalary: getJobPreference()?.expectedAnnualSalary,
			}))(),
		}),
		createRegistrationStep({
			component:  RegistrationSteps.DesiredJobOccupation,
			formSubmit: actions.submitDesiredJobOccupation,
			key:        'desired-job-occupation',
			schema:     RegistrationStepSchema.DesiredJobOccupationStepSchema as never,
			title:      'Please enter your desired job occupation',
		}),
		createRegistrationStep({
			component: RegistrationSteps.DesiredJobLocation,
			key:       'desired-job-location',
			schema:    RegistrationStepSchema.DesiredJobLocationStepSchema,
			title:     'Please enter your desired job location',

			formSubmit: async (data: RegistrationStepSchema.DesiredJobLocationStep) => {
				actions.submitDesiredJobLocation(data);
				await execute();
			},
		}),
	];

	return { registrationSteps };
};
